import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { AppLayout } from "@/components/layout/AppLayout";
import { RequireAuth } from "@/components/RequireAuth";
import ArticleDetail        from "@/pages/ArticleDetail";
import Articles             from "@/pages/Articles";
import AuditLog             from "@/pages/AuditLog";
import Categories           from "@/pages/Categories";
import ClimateProjectDetail from "@/pages/ClimateProjectDetail";
import ClimateProjects      from "@/pages/ClimateProjects";
import ContactInbox        from "@/pages/ContactInbox";
import ContactInfoPage     from "@/pages/ContactInfoPage";
import Dashboard       from "@/pages/Dashboard";
import Departments     from "@/pages/Departments";
import DocumentDetail  from "@/pages/DocumentDetail";
import DocumentLibrary from "@/pages/DocumentLibrary";
import EventDetail     from "@/pages/EventDetail";
import Events          from "@/pages/Events";
import FAQs            from "@/pages/FAQs";
import Forum           from "@/pages/Forum";
import Gallery         from "@/pages/Gallery";
import IndicatorDetail from "@/pages/IndicatorDetail";
import Indicators      from "@/pages/Indicators";
import Login           from "@/pages/Login";
import Moderation      from "@/pages/Moderation";
import News            from "@/pages/News";
import Notifications   from "@/pages/Notifications";
import PrivateLibrary  from "@/pages/PrivateLibrary";
import Profile         from "@/pages/Profile";
import Search          from "@/pages/Search";
import SetPassword     from "@/pages/SetPassword";
import Tags            from "@/pages/Tags";
import ThreadDetail    from "@/pages/ThreadDetail";
import Messages        from "@/pages/Messages";
import Users           from "@/pages/Users";
import Verification    from "@/pages/Verification";

/**
 * Router shell.
 *
 * - /login                — public; the React-side replacement for
 *                           Django's old sign-in page.
 * - everything else       — wrapped in RequireAuth + AppLayout
 *                           (sidebar + topbar shell).
 *
 * Real pages: Dashboard, Library, Articles, Events, Verification,
 * Users, Notifications, Tags. Every sidebar entry resolves to a
 * working surface — placeholders are gone.
 */
export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public */}
        <Route path="/login" element={<Login />} />
        {/* Invite-email landing page — no auth required, by definition. */}
        <Route path="/set-password/:uidb64/:token" element={<SetPassword />} />

        {/* Authenticated shell */}
        <Route element={<RequireAuth><AppLayout /></RequireAuth>}>
          <Route path="/"               element={<Dashboard />} />
          <Route path="/library"        element={<DocumentLibrary />} />
          <Route path="/library/:id"    element={<DocumentDetail />} />
          <Route path="/private"        element={<PrivateLibrary />} />
          <Route path="/articles"       element={<Articles />} />
          <Route path="/articles/:slug" element={<ArticleDetail />} />
          <Route path="/events"         element={<Events />} />
          <Route path="/events/:id"     element={<EventDetail />} />
          <Route path="/indicators"     element={<Indicators />} />
          <Route path="/indicators/:id" element={<IndicatorDetail />} />
          <Route path="/climate-projects"     element={<ClimateProjects />} />
          <Route path="/climate-projects/:id" element={<ClimateProjectDetail />} />
          <Route path="/forum"             element={<Forum />} />
          <Route path="/forum/threads/:id" element={<ThreadDetail />} />
          <Route path="/messages"          element={<Messages />} />
          <Route path="/messages/:id"      element={<Messages />} />
          <Route path="/notifications"  element={<Notifications />} />
          <Route path="/profile"        element={<Profile />} />
          <Route path="/search"         element={<Search />} />

          {/* Director+ surfaces — Sidebar already hides these for
              regular users; the API enforces the same gate on every
              request. RequireAuth on the parent admits any signed-in
              user, so we re-wrap the director-only pages with the
              minRole prop for belt-and-braces. */}
          <Route path="/users"         element={<RequireAuth minRole="director"><Users /></RequireAuth>} />
          <Route path="/verification"  element={<RequireAuth minRole="director"><Verification /></RequireAuth>} />
          <Route path="/moderation"    element={<RequireAuth minRole="director"><Moderation /></RequireAuth>} />
          <Route path="/tags"          element={<RequireAuth minRole="director"><Tags /></RequireAuth>} />
          <Route path="/categories"    element={<RequireAuth minRole="director"><Categories /></RequireAuth>} />
          <Route path="/departments"   element={<RequireAuth minRole="ceo"><Departments /></RequireAuth>} />
          <Route path="/contact-inbox" element={<RequireAuth minRole="director"><ContactInbox /></RequireAuth>} />
          <Route path="/gallery"       element={<RequireAuth minRole="admin"><Gallery /></RequireAuth>} />
          <Route path="/faqs"          element={<RequireAuth minRole="admin"><FAQs /></RequireAuth>} />
          <Route path="/news"          element={<RequireAuth minRole="ceo"><News /></RequireAuth>} />
          <Route path="/contact-info"  element={<RequireAuth minRole="admin"><ContactInfoPage /></RequireAuth>} />
          <Route path="/audit-log"     element={<RequireAuth minRole="admin"><AuditLog /></RequireAuth>} />
        </Route>

        {/* Anything unmatched bounces home. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
